import { Button } from "@foodpilot/foods";
import { Stack, useTheme, useMediaQuery } from "@mui/material";
import { Typography as FoodsTypography } from "@foodpilot/foods";
import { useState, useMemo, useContext } from "react";
import { t } from "i18next";
import { buildPuckPdfExportUrl, getPdfExportOptionsFromPuckRootProps, usePdfExport } from "src/hooks/usePdfExport";
import { RootPropsContext } from "src/components/Puck/config/root";
import { isAppInPDFMode } from "src/utils/rendering";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useTokenResolver } from "src/components/Puck/config/fields/extensions/typingSuggestions/hooks/useTokenResolver";

export type PdfExportProps = {
    label?: string;
    url?: string;
    width?: "auto" | "full" | "half" | "default";
    position?: "left" | "center" | "right";
    variant?: TypographyVariant;
    textColor?: string;
};

export type TypographyVariant =
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "subtitle1"
    | "subtitle2"
    | "body1"
    | "body2"
    | "caption"
    | "overline";

const PdfExport = (props: PdfExportProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { exportPage, isExporting } = usePdfExport();
    const [error, setError] = useState<string | null>(null);
    const rootProps = useContext(RootPropsContext);
    const foodpilotContext = useFoodpilotContext();

    const {
        label = "puck.blocks.pdf_export.label",
        url = "/",
        width = "default",
        position = "left",
        variant = "body1",
        textColor = "#ffffff",
    } = props;

    const { processToken } = useTokenResolver();

    const shouldHide =
        isAppInPDFMode() ||
        (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("from") === "templates");

    const isTranslationKey = useMemo(() => {
        return typeof label === "string" && label.trim() !== "" && !label.includes(" ") && !label.includes("\n");
    }, [label]);

    const displayLabel = useMemo(() => {
        return isTranslationKey ? t(label, "Export to PDF") : label;
    }, [isTranslationKey, label]);

    if (shouldHide) return null;

    const widthMap = {
        default: "fit-content",
        auto: "auto",
        full: "100%",
        half: "50%",
    };

    const positionMap = {
        left: "flex-start",
        center: "center",
        right: "flex-end",
    };

    const handleExport = async () => {
        setError(null);
        try {
            // Générer un nom de fichier depuis l'URL
            const filename = generateFilename(url);

            const urlWithParams = buildPuckPdfExportUrl(url, {
                selectedCompanyId: foodpilotContext?.selectedCompany?.id,
                score: rootProps?.score,
            });
            const options = getPdfExportOptionsFromPuckRootProps(rootProps);

            // Resolve tokens in header and footer before export
            if (options.pdfHeader) {
                options.pdfHeader = {
                    ...options.pdfHeader,
                    logoUrl: processToken(options.pdfHeader.logoUrl),
                    title: processToken(options.pdfHeader.title),
                    subtitle: processToken(options.pdfHeader.subtitle),
                };
            }
            if (options.pdfFooter) {
                options.pdfFooter = {
                    ...options.pdfFooter,
                    text: processToken(options.pdfFooter.text),
                };
            }

            await exportPage(urlWithParams, filename, options);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Export error");
            console.error("PDF export error:", err);
        }
    };

    const generateFilename = (path: string): string => {
        const cleanPath = path.replace(/^\/+|\/+$/g, "").replace(/\//g, "-");
        const filename = cleanPath || "export";
        return `${filename}-${new Date().toISOString().split("T")[0]}.pdf`;
    };

    return (
        <Stack
            sx={{
                alignItems: positionMap[position],
                width: "100%",
            }}
        >
            <Button
                onClick={handleExport}
                disabled={isExporting}
                variant="primary"
                sx={{
                    width: widthMap[width],
                    color: textColor,
                    padding: isMobile ? theme.spacing(2) : theme.spacing(3),
                    opacity: isExporting ? 0.6 : 1,
                    cursor: isExporting ? "wait" : "pointer",
                }}
            >
                <FoodsTypography
                    sx={{
                        color: textColor,
                        fontWeight: variant === "body1" ? 600 : theme.typography.fontWeightBold,
                    }}
                    variant={variant}
                >
                    {isExporting ? t("puck.blocks.pdf_export.generating", "Generating...") : displayLabel}
                </FoodsTypography>
            </Button>
            {error && (
                <FoodsTypography
                    variant="caption"
                    sx={{
                        color: theme.palette.error.main,
                        marginTop: theme.spacing(1),
                    }}
                >
                    {error}
                </FoodsTypography>
            )}
        </Stack>
    );
};

export default PdfExport;
